import React, { lazy, Suspense } from 'react';
import { Route, Switch, withRouter, Redirect, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Route Guards
import { PublicRoute } from './routeguards/PublicRoute';
import { PrivateRoute } from './routeguards/PrivateRoute';
import { CompanyRoute } from './routeguards/CompanyRoute.jsx';

// Providers
import { AuthProvider, useAuth } from './providers/AuthProvider';
import { SettingsProvider } from './context/SettingsContext';
import { FeatureProvider } from './context/FeatureContext';
import { CapabilityProvider } from './context/CapabilityContext';
import { InboxProvider } from './context/InboxContext';

import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";

// Components
import Index from "./jsx";
import SelectCompany from './jsx/pages/SelectCompany';
import NewCompany from './jsx/pages/NewCompany';
import AcceptInvitation from './jsx/pages/AcceptInvite';
import PublicQuoteView from './jsx/components/AppsMenu/Quotes/PublicQuoteView';
import { InvoiceSettingsProvider } from './context/InvoiceSettingsContext.js';
import { AccountingSettingsProvider } from './context/AccountingSettingsContext.js';
import { QuoteSettingsProvider } from "./context/QuoteSettingsContext";

// Lazy-loaded components
const SignUp = lazy(() => import('./jsx/pages/Registration'));
const ForgotPassword = lazy(() => import('./jsx/pages/ForgotPassword'));
const Login = lazy(() => import('./jsx/pages/Login'));
// REMOVED: SignupCodesAdmin - moved to ledgerloop-admin app

const AuthenticatedApp = ({ children }) => {
  return (
    <SettingsProvider>
      <FeatureProvider>
        <CapabilityProvider>
          <InvoiceSettingsProvider>
            <QuoteSettingsProvider>
              <AccountingSettingsProvider>
                <InboxProvider>
                  {children}
                </InboxProvider>
              </AccountingSettingsProvider>
            </QuoteSettingsProvider>
          </InvoiceSettingsProvider>
        </CapabilityProvider>
      </FeatureProvider>
    </SettingsProvider>
  );
};

const MainAppComponent = () => (
  <AuthenticatedApp>
    <Index />
  </AuthenticatedApp>
);

const isPublicRoute = (pathname) => {
  const publicRoutes = [
    '/login',
    '/page-register',
    '/page-forgot-password'
  ];

  if (publicRoutes.includes(pathname)) {
    return true;
  }

  if (pathname.includes('/public/quote/view/') || pathname.includes('/invite')) {
    return true;
  }

  return false;
};

const AppContent = () => {
  const { isInitialized, isLoading } = useAuth();
  const location = useLocation();
  // Selectors read the canonical reducer keys: `isAuthenticated` and
  // `isCompanySelected` (Selector helpers also exist in
  // src/store/selectors/AuthSelectors.js — use those instead of inline reads
  // when adding new consumers). Pre-2026-05-14 this read
  // `state.auth.companyIsSelected` (note swapped `is`/`Is`), which was
  // always undefined → falsy → fallback redirect at line ~145 always fell
  // into "no company selected." Masked because <CompanyRoute path="/">
  // above this catch-all handles selection; flagged in 2026-05-14 review.
  const userIsAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const companyIsSelected = useSelector((state) => state.auth.isCompanySelected);

  // Show loading while authentication is being initialized
  if (!isInitialized || isLoading) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
        <div className="ms-3">Initializing application...</div>
      </div>
    );
  }

  return (
    <div className="vh-100">
      <Suspense fallback={
        <div id="preloader">
          <div className="sk-three-bounce">
            <div className="sk-child sk-bounce1"></div>
            <div className="sk-child sk-bounce2"></div>
            <div className="sk-child sk-bounce3"></div>
          </div>
        </div>
      }>
        <Switch>
          {/* Public Routes - No authentication required */}
          <PublicRoute
            path="/:companyId/public/quote/view/:token"
            component={PublicQuoteView}
          />
          <PublicRoute
            path="/:companyId/invite"
            component={AcceptInvitation}
          />

          {/* Authentication Routes */}
          <PublicRoute path="/login" component={Login} />
          <PublicRoute path="/page-register" component={SignUp} />
          <PublicRoute path="/forgot-password" component={ForgotPassword} />

          {/* Company Management Routes */}
          <PrivateRoute path="/create-company" component={NewCompany} />
          <PrivateRoute path="/select-company" component={SelectCompany} />

          {/* Platform Admin Routes - REMOVED: moved to ledgerloop-admin app */}

          {/* Main Application Routes */}
          <CompanyRoute
            path="/"
            component={MainAppComponent}
          />

          {/* Default Redirects - Only if no routes matched */}
          <Route
            exact
            path="/"
            render={() => {
              if (!userIsAuthenticated) {
                return <Redirect to="/login" />;
              }
              if (!companyIsSelected) {
                return <Redirect to="/select-company" />;
              }
              // If user is authenticated and has company selected, redirect to dashboard
              const storedCompany = localStorage.getItem('selectedCompany');
              if (storedCompany) {
                const { companyId } = JSON.parse(storedCompany);
                return <Redirect to={`/${companyId}/dashboard`} />;
              }
              return <Redirect to="/select-company" />;
            }}
          />
        </Switch>
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default withRouter(App);
